import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { removeRetailerTokenFromUrl } from "@faire/retailer-visitor-shared/lib/removeRetailerTokenFromUrl";
import { useChatBubbleStore } from "@faire/retailer-visitor-shared/packages/messenger/views/useChatBubbleStore";
import { getDocumentOrThrow } from "@faire/web--source/common/globals/getDocument";
import { getLocation } from "@faire/web--source/common/globals/getLocation";
import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
var noIndexStashedElements = [
    "link[rel=next]:not([data-react-helmet])",
    "link[rel=prev]:not([data-react-helmet])"
];
var descriptionStashedElements = [
    "meta[name=description]:not([data-react-helmet])"
];
/**
 * Generally used for logged out pages to hence SEO + the crawlers
 * ability to index pages
 */ export var FaireHelmet = function(param) {
    var title = param.title, image = param.image, prev = param.prev, next = param.next, noindex = param.noindex, description = param.description, selfCanonical = param.selfCanonical, alignOpenGraphTitleDescription = param.alignOpenGraphTitleDescription;
    var _getLocation, _getLocation1, _image_url;
    var _useState = _sliced_to_array(useState(""), 2), oldTitle = _useState[0], setOldTitle = _useState[1];
    var _useState1 = _sliced_to_array(useState([]), 2), stashedElements = _useState1[0], setStashedElements = _useState1[1];
    var _useChatBubbleStore = _sliced_to_array(useChatBubbleStore([]), 2), setChatBubbleStore = _useChatBubbleStore[1];
    useEffect(function() {
        //Since we already set up the og:title and description in indexHead.tsx,
        // we should change the existing one instead creating a new one
        if (alignOpenGraphTitleDescription && title && description) {
            var _getDocumentOrThrow_querySelector, _getDocumentOrThrow_querySelector1;
            (_getDocumentOrThrow_querySelector = getDocumentOrThrow().querySelector('meta[property="og:title"]')) === null || _getDocumentOrThrow_querySelector === void 0 ? void 0 : _getDocumentOrThrow_querySelector.setAttribute("content", title);
            (_getDocumentOrThrow_querySelector1 = getDocumentOrThrow().querySelector('meta[property="og:description"]')) === null || _getDocumentOrThrow_querySelector1 === void 0 ? void 0 : _getDocumentOrThrow_querySelector1.setAttribute("content", description);
        }
    }, [
        alignOpenGraphTitleDescription,
        description,
        title
    ]);
    useEffect(function() {
        setOldTitle(getDocumentOrThrow().title);
    }, []);
    useEffect(function() {
        // hack to force chrome tab to update correctly
        getDocumentOrThrow().title = "".concat(title || oldTitle, ".");
        getDocumentOrThrow().title = title || oldTitle;
        setChatBubbleStore({
            tabTitle: title !== null && title !== void 0 ? title : undefined
        });
        return function() {
            getDocumentOrThrow().title = oldTitle;
            setChatBubbleStore({
                tabTitle: undefined
            });
        };
    }, [
        title,
        setChatBubbleStore,
        oldTitle,
        stashedElements
    ]);
    var stashElements = useCallback(function(elementsToStash) {
        var afterTimeout = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : true;
        var elementsToRemove = getDocumentOrThrow().head.querySelectorAll(elementsToStash.join(","));
        if (elementsToRemove.length === 0) {
            return;
        }
        var stash = function() {
            setStashedElements(function(stashedElements) {
                return stashedElements.concat(Array.from(elementsToRemove));
            });
            elementsToRemove.forEach(function(element) {
                return element.remove();
            });
        };
        if (afterTimeout) {
            setTimeoutIfPossible(stash, 0);
        } else {
            stash();
        }
    }, []);
    useEffect(function() {
        if (stashedElements.length > 0) {
            return;
        }
        if (noindex) {
            stashElements(noIndexStashedElements);
        }
        if (description) {
            stashElements(descriptionStashedElements, false);
        }
        return function() {
            if (stashedElements.length > 0) {
                stashedElements.forEach(function(element) {
                    getDocumentOrThrow().head.appendChild(element);
                });
            }
        };
    }, [
        description,
        noindex,
        stashElements,
        stashedElements
    ]);
    var urlWithoutParams = (((_getLocation = getLocation()) === null || _getLocation === void 0 ? void 0 : _getLocation.origin) || "") + (((_getLocation1 = getLocation()) === null || _getLocation1 === void 0 ? void 0 : _getLocation1.pathname) || "");
    return /*#__PURE__*/ _jsxs(Helmet, {
        children: [
            title ? /*#__PURE__*/ _jsx("title", {
                "data-faire-helmet": "true",
                children: title
            }) : null,
            (image === null || image === void 0 ? void 0 : image.url) ? /*#__PURE__*/ _jsx("meta", {
                property: "og:image",
                content: image.url
            }) : null,
            (image === null || image === void 0 ? void 0 : image.width) ? /*#__PURE__*/ _jsx("meta", {
                property: "og:image:width",
                content: image.width.toString()
            }) : null,
            (image === null || image === void 0 ? void 0 : image.height) ? /*#__PURE__*/ _jsx("meta", {
                property: "og:image:height",
                content: image.height.toString()
            }) : null,
            (image === null || image === void 0 ? void 0 : (_image_url = image.url) === null || _image_url === void 0 ? void 0 : _image_url.endsWith(".png")) ? /*#__PURE__*/ _jsx("meta", {
                property: "og:image:type",
                content: "image/png"
            }) : null,
            prev ? /*#__PURE__*/ _jsx("link", {
                rel: "prev",
                href: "".concat(removeRetailerTokenFromUrl(urlWithoutParams)).concat(prev > 1 ? "?page=".concat(prev) : "")
            }) : null,
            next ? /*#__PURE__*/ _jsx("link", {
                rel: "next",
                href: "".concat(removeRetailerTokenFromUrl(urlWithoutParams), "?page=").concat(next)
            }) : null,
            noindex ? /*#__PURE__*/ _jsx("meta", {
                name: "robots",
                content: "noindex"
            }) : null,
            description ? /*#__PURE__*/ _jsx("meta", {
                content: description,
                name: "description"
            }) : null,
            selfCanonical ? /*#__PURE__*/ _jsx("link", {
                href: urlWithoutParams,
                rel: "canonical"
            }) : null
        ]
    });
};
